import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatRelativeTime, classNames, debounce } from '../lib/utils';
import {
  Filter,
  Search,
  Download,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Tag as TagIcon,
  Database,
  Server,
  Box,
  HardDrive,
  Network,
  Cpu,
  AlertCircle,
  Trash2,
  X,
  CheckCircle,
} from 'lucide-react';
import type { Resource, ResourcesQueryParams, DeleteResourceResult } from '../lib/types';
import { DELETABLE_TYPES, resourceKey, formatCostPerDay, MAX_BULK_DELETE } from '../lib/resourceHelpers';

const RESOURCE_TYPE_ICONS: Record<string, React.ElementType> = {
  'ec2-instance': Server,
  'rds-instance': Database,
  'lambda-function': Box,
  'ebs-volume': HardDrive,
  'elb': Network,
  'asg': Cpu,
};

export function ResourcesPage() {
  const { idToken: accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [error, setError] = useState('');
  const pageSize = 50;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState<DeleteResourceResult[] | null>(null);

  // DynamoDB pagination is cursor-based, not offset-based - track a history stack of previous
  // cursors so "Previous" can pop back, and the next_cursor from the last response for "Next".
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

  // Filters
  const [filters, setFilters] = useState<ResourcesQueryParams>({});
  const [searchTerm, setSearchTerm] = useState('');
  const resetToFirstPage = () => {
    setCursor(undefined);
    setCursorStack([]);
  };
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      setFilters((prev) => ({ ...prev, search: term || undefined }));
      resetToFirstPage();
    }, 300),
    []
  );

  const fetchResources = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');

    try {
      const params = { ...filters, limit: pageSize, cursor };
      const res = await api.resources.list(accessToken, params);
      setResources(res.resources);
      setNextCursor(res.next_cursor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load resources');
      setResources([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, filters, pageSize, cursor]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const goToNextPage = () => {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, cursor ?? '']);
    setCursor(nextCursor);
  };
  const goToPrevPage = () => {
    setCursorStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const previous = stack.pop();
      setCursor(previous || undefined);
      return stack;
    });
  };

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Selection is keyed by "account_id#resource_id" (resource_id/ARN alone isn't unique across
  // accounts). Cleared whenever the visible page changes so stale selections from a previous
  // filter/page can't linger into a delete confirmation for resources no longer on screen.
  useEffect(() => {
    setSelected(new Set());
  }, [resources]);

  const deletableOnPage = useMemo(
    () => resources.filter((r) => DELETABLE_TYPES.has(r.resource_type)),
    [resources],
  );
  const allDeletableSelected = deletableOnPage.length > 0 && deletableOnPage.every((r) => selected.has(resourceKey(r)));

  const toggleSelectAll = () => {
    if (allDeletableSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(deletableOnPage.slice(0, MAX_BULK_DELETE).map(resourceKey)));
    }
  };

  const toggleSelectOne = (resource: Resource) => {
    setSelected((prev) => {
      const key = resourceKey(resource);
      if (prev.has(key)) {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }
      if (prev.size >= MAX_BULK_DELETE) return prev;
      return new Set(prev).add(key);
    });
  };

  const selectedResources = useMemo(
    () => resources.filter((r) => selected.has(resourceKey(r))),
    [resources, selected],
  );
  const selectedDailyCost = useMemo(
    () => selectedResources.reduce((sum, r) => sum + (r.estimated_cost_usd_per_day || 0), 0),
    [selectedResources],
  );

  const handleDeleteSelected = async () => {
    if (!accessToken || selectedResources.length === 0) return;
    setDeleting(true);
    try {
      const items = selectedResources.map((r) => ({ account_id: r.account_id, resource_id: r.resource_id }));
      const res = await api.resources.deleteMany(accessToken, items);
      setDeleteResults(res.results);
      const deletedKeys = new Set(
        res.results.filter((r) => r.status === 'deleted').map((r) => resourceKey(r)),
      );
      setResources((prev) => prev.filter((r) => !deletedKeys.has(resourceKey(r))));
      setSelected(new Set());
    } catch (err) {
      setDeleteResults(
        selectedResources.map((r) => ({
          account_id: r.account_id,
          resource_id: r.resource_id,
          status: 'failed' as const,
          detail: err instanceof ApiError ? err.message : 'Delete request failed',
        })),
      );
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Extract unique filter options from resources
  const accounts = useMemo(() => [...new Set(resources.map((r) => r.account_id))].sort(), [resources]);
  const regions = useMemo(() => [...new Set(resources.map((r) => r.region))].sort(), [resources]);
  const resourceTypes = useMemo(() => [...new Set(resources.map((r) => r.resource_type))].sort(), [resources]);
  const states = useMemo(() => [...new Set(resources.map((r) => r.state).filter(Boolean))].sort(), [resources]);

  // Get all tag keys, and the distinct values seen for each - computed together in one pass so
  // the per-tag-key dropdown below can do a plain lookup instead of calling useMemo inside a
  // .map() loop (that was the actual crash: a variable number of hook calls per render, which
  // React rejects outright once any resource has tags - "Rendered more hooks than during the
  // previous render").
  const { tagKeys, tagValuesByKey } = useMemo(() => {
    const valuesByKey = new Map<string, Set<string>>();
    resources.forEach((r) => {
      Object.entries(r.tags).forEach(([k, v]) => {
        if (!v) return;
        if (!valuesByKey.has(k)) valuesByKey.set(k, new Set());
        valuesByKey.get(k)!.add(v);
      });
    });
    const keys = [...valuesByKey.keys()].sort();
    const values: Record<string, string[]> = {};
    for (const k of keys) values[k] = [...valuesByKey.get(k)!].sort();
    return { tagKeys: keys, tagValuesByKey: values };
  }, [resources]);

  const handleFilterChange = (key: 'account_id' | 'region' | 'resource_type' | 'state', value: string | undefined) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    resetToFirstPage();
  };

  const handleTagFilterChange = (tagKey: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      const tagFilterKey = `tag.${tagKey}` as const;
      if (value) {
        next[tagFilterKey] = value;
      } else {
        delete next[tagFilterKey];
      }
      return next;
    });
    resetToFirstPage();
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    resetToFirstPage();
  };

  const hasActiveFilters = useMemo(() => {
    return Object.keys(filters).some((k) => k !== 'limit' && filters[k as keyof ResourcesQueryParams]) || searchTerm;
  }, [filters, searchTerm]);

  if (isLoading && resources.length === 0) {
    return (
      <div className="page-resources">
        <div className="page-header">
          <h1 className="page-title">Resources</h1>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="empty-state">
              <Loader2 size={48} className="empty-state-icon spin" />
              <h3 className="empty-state-title">Loading resources...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-resources">
      <header className="page-header">
        <h1 className="page-title">Resources</h1>
        <div className="page-header-actions">
          {selected.size > 0 && (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} disabled={deleting} title={`Up to ${MAX_BULK_DELETE} at a time`}>
              <Trash2 size={18} /> Delete {selected.size} selected
            </button>
          )}
          <button className="btn btn-ghost btn-icon" onClick={fetchResources} disabled={isLoading} aria-label="Refresh">
            <RefreshCw size={20} className={isLoading ? 'spin' : ''} />
          </button>
          <button className="btn btn-secondary">
            <Download size={18} /> Export CSV
          </button>
        </div>
      </header>

      {deleteResults && (
        <div
          className="login-alert"
          style={{
            marginBottom: 'var(--space-4)',
            background: deleteResults.every((r) => r.status === 'deleted') ? 'var(--color-primary-light)' : undefined,
            borderColor: deleteResults.every((r) => r.status === 'deleted') ? 'var(--color-primary)' : undefined,
            color: deleteResults.every((r) => r.status === 'deleted') ? 'var(--color-primary-dark)' : undefined,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--space-1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {deleteResults.every((r) => r.status === 'deleted') ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <strong>
              {deleteResults.filter((r) => r.status === 'deleted').length} deleted
              {deleteResults.some((r) => r.status === 'failed') &&
                `, ${deleteResults.filter((r) => r.status === 'failed').length} failed`}
            </strong>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setDeleteResults(null)} aria-label="Dismiss" style={{ marginLeft: 'auto' }}>
              <X size={14} />
            </button>
          </div>
          {deleteResults.filter((r) => r.status === 'failed').map((r) => (
            <p key={resourceKey(r)} style={{ fontSize: 'var(--text-xs)' }}>
              {r.resource_id}: {r.detail}
            </p>
          ))}
        </div>
      )}

      {error && (
        <div className="login-alert" style={{ marginBottom: 'var(--space-4)' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Filters Bar */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="card-content" style={{ padding: 'var(--space-4)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
            {/* Search */}
            <div className="form-group" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
              <label htmlFor="search" className="form-label" style={{ display: 'none' }}>Search resources</label>
              <div className="form-input-wrapper">
                <Search size={18} className="form-input-icon" aria-hidden="true" />
                <input
                  type="text"
                  id="search"
                  className="form-input"
                  placeholder="Search by name, ID, ARN..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Account Filter */}
            <div className="form-group" style={{ minWidth: 180, marginBottom: 0 }}>
              <label htmlFor="filter-account" className="form-label">Account</label>
              <select
                id="filter-account"
                className="form-select"
                value={filters.account_id || ''}
                onChange={(e) => handleFilterChange('account_id', e.target.value || undefined)}
                disabled={isLoading}
              >
                <option value="">All Accounts</option>
                {accounts.map((acc) => (
                  <option key={acc} value={acc}>{acc}</option>
                ))}
              </select>
            </div>

            {/* Region Filter */}
            <div className="form-group" style={{ minWidth: 160, marginBottom: 0 }}>
              <label htmlFor="filter-region" className="form-label">Region</label>
              <select
                id="filter-region"
                className="form-select"
                value={filters.region || ''}
                onChange={(e) => handleFilterChange('region', e.target.value || undefined)}
                disabled={isLoading}
              >
                <option value="">All Regions</option>
                {regions.map((reg) => (
                  <option key={reg} value={reg}>{reg}</option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div className="form-group" style={{ minWidth: 180, marginBottom: 0 }}>
              <label htmlFor="filter-type" className="form-label">Type</label>
              <select
                id="filter-type"
                className="form-select"
                value={filters.resource_type || ''}
                onChange={(e) => handleFilterChange('resource_type', e.target.value || undefined)}
                disabled={isLoading}
              >
                <option value="">All Types</option>
                {resourceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            {/* State Filter */}
            <div className="form-group" style={{ minWidth: 160, marginBottom: 0 }}>
              <label htmlFor="filter-state" className="form-label">State</label>
              <select
                id="filter-state"
                className="form-select"
                value={filters.state || ''}
                onChange={(e) => handleFilterChange('state', e.target.value || undefined)}
                disabled={isLoading}
              >
                <option value="">All States</option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Tag Filters */}
            {tagKeys.slice(0, 3).map((tagKey) => (
              <div key={tagKey} className="form-group" style={{ minWidth: 160, marginBottom: 0 }}>
                <label htmlFor={`filter-tag-${tagKey}`} className="form-label">
                  <TagIcon size={14} /> {tagKey}
                </label>
                <select
                  id={`filter-tag-${tagKey}`}
                  className="form-select"
                  value={filters[`tag.${tagKey}` as keyof ResourcesQueryParams] || ''}
                  onChange={(e) => handleTagFilterChange(tagKey, e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">All</option>
                  {(tagValuesByKey[tagKey] || []).map((val) => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>
              </div>
            ))}

            {hasActiveFilters && (
              <button className="btn btn-ghost" onClick={clearFilters} style={{ marginLeft: 'auto' }}>
                <Filter size={16} /> Clear Filters
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--surface-border)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {Object.entries(filters).filter(([k]) => k !== 'limit' && k !== 'offset').map(([key, value]) => (
                  <span key={key} className="badge badge-info" style={{ fontSize: 'var(--text-xs)' }}>
                    {key}: {Array.isArray(value) ? value.join(', ') : value}
                  </span>
                ))}
                {searchTerm && (
                  <span className="badge badge-info" style={{ fontSize: 'var(--text-xs)' }}>
                    Search: {searchTerm}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resources Table */}
      <div className="card">
        {resources.length === 0 ? (
          <div className="card-content empty-state">
            <Database size={64} className="empty-state-icon" />
            <h3 className="empty-state-title">No resources found</h3>
            <p className="empty-state-text">
              {hasActiveFilters ? 'Try adjusting your filters' : 'Run a scan to discover resources in your accounts'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        aria-label="Select all deletable resources"
                        checked={allDeletableSelected}
                        onChange={toggleSelectAll}
                        disabled={deletableOnPage.length === 0}
                      />
                    </th>
                    <th>Resource</th>
                    <th>Type</th>
                    <th>Account</th>
                    <th>Region</th>
                    <th>State</th>
                    <th>Utilization</th>
                    <th>Tags</th>
                    <th>Age</th>
                    <th>Est. Cost</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {resources.map((resource) => {
                    const key = resourceKey(resource);
                    const isDeletable = DELETABLE_TYPES.has(resource.resource_type);
                    return (
                    <tr key={key}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${resource.name || resource.resource_id}`}
                          checked={selected.has(key)}
                          onChange={() => toggleSelectOne(resource)}
                          disabled={!isDeletable}
                          title={isDeletable ? undefined : 'Delete not supported for this resource type - remove manually'}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                          {(() => {
                            const Icon = RESOURCE_TYPE_ICONS[resource.resource_type] || Database;
                            return <Icon size={18} style={{ color: 'var(--clr-teal)' }} />;
                          })()}
                          <div>
                            <p style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>
                              {resource.name || truncate(resource.resource_id, 50)}
                            </p>
                            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)', fontFamily: 'monospace' }}>
                              {truncate(resource.resource_id, 60)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-neutral" style={{ textTransform: 'none' }}>
                          {resource.resource_type.replace(/-/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <code style={{ fontSize: 'var(--text-xs)' }}>{resource.account_id}</code>
                      </td>
                      <td>
                        <span className="badge badge-info" style={{ fontSize: 'var(--text-xs)' }}>
                          {resource.region}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                          <span className={classNames('status-dot', resource.state || 'unknown')} />
                          <span style={{ fontSize: 'var(--text-sm)', textTransform: 'capitalize' }}>
                            {resource.state || 'unknown'}
                          </span>
                        </div>
                      </td>
                      <td>
                        {resource.utilization.cpu_avg_pct !== undefined && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <div style={{
                              width: 60,
                              height: 6,
                              background: 'var(--slate-200)',
                              borderRadius: 'var(--radius-pill)',
                              overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${Math.min(100, Math.max(0, resource.utilization.cpu_avg_pct))}%`,
                                height: '100%',
                                background: resource.utilization.cpu_avg_pct > 80 ? 'var(--color-red)' : resource.utilization.cpu_avg_pct > 50 ? 'var(--color-amber)' : 'var(--color-primary)',
                                borderRadius: 'var(--radius-pill)',
                                transition: 'width 0.3s ease',
                              }} />
                            </div>
                            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--slate-700)' }}>
                              {resource.utilization.cpu_avg_pct.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {!resource.utilization.cpu_avg_pct && (
                          <span style={{ color: 'var(--slate-400)', fontSize: 'var(--text-xs)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                          {Object.entries(resource.tags).slice(0, 3).map(([k, v]) => (
                            <span key={k} className="badge badge-neutral" style={{ fontSize: 'var(--text-xs)' }}>
                              {k}: {v}
                            </span>
                          ))}
                          {Object.keys(resource.tags).length > 3 && (
                            <span className="badge badge-neutral" style={{ fontSize: 'var(--text-xs)' }}>
                              +{Object.keys(resource.tags).length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)' }}>
                        {resource.created_at ? formatRelativeTime(resource.created_at) : '—'}
                      </td>
                      <td style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: (resource.estimated_cost_usd_per_day || 0) > 0 ? 'var(--color-amber-dark)' : 'var(--slate-500)' }}>
                        {formatCostPerDay(resource.estimated_cost_usd_per_day)}
                      </td>
                      <td style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)' }}>
                        {formatRelativeTime(resource.last_seen_at)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(cursorStack.length > 0 || nextCursor) && (
              <div className="card-footer" style={{ justifyContent: 'space-between' }}>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-600)' }}>
                  Page {cursorStack.length + 1} · {resources.length} resources shown
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    onClick={goToPrevPage}
                    disabled={cursorStack.length === 0 || isLoading}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    className="btn btn-ghost btn-icon btn-sm"
                    onClick={goToNextPage}
                    disabled={!nextCursor || isLoading}
                    aria-label="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)} role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h2 id="delete-confirm-title" className="modal-title">Delete {selectedResources.length} resources?</h2>
              <button className="modal-close" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="modal-body">
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-600)', marginBottom: 'var(--space-4)' }}>
                This permanently deletes the resources below from AWS - not just from this list.
                Only accounts with delete explicitly enabled (Settings → Accounts) will succeed;
                others will report why they were skipped.
              </p>
              <div className="table-container" style={{ maxHeight: 280, overflowY: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Type</th>
                      <th>Account</th>
                      <th>Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedResources.map((r) => (
                      <tr key={resourceKey(r)}>
                        <td style={{ fontSize: 'var(--text-xs)' }}>{r.name || truncate(r.resource_id, 40)}</td>
                        <td style={{ fontSize: 'var(--text-xs)' }}>{r.resource_type}</td>
                        <td><code style={{ fontSize: 'var(--text-xs)' }}>{r.account_id}</code></td>
                        <td style={{ fontSize: 'var(--text-xs)' }}>{formatCostPerDay(r.estimated_cost_usd_per_day)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedDailyCost > 0 && (
                <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-primary-dark)' }}>
                  Estimated savings: ${selectedDailyCost.toFixed(2)}/day
                </p>
              )}
            </div>
            <footer className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteSelected} disabled={deleting}>
                {deleting ? (
                  <>
                    <Loader2 size={18} className="spin" /> Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} /> Delete {selectedResources.length} resources
                  </>
                )}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper for truncate
function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length - 1) + '…';
}